/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {PdfJsViewerComponent} from 'ng2-pdfjs-viewer';
import {Subject} from 'rxjs';
import {filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {FileService} from '../files/file.service';
import {FileDescription, FileDescriptionsWithTotal} from '../files/files.model';
import {DefaultPagination, Pagination} from '../ui/pagination.component';
import {SortDirection} from '../ui/table/table.constants';
import {SortKey} from '../ui/table/table.interfaces';
import {Files} from '../utils/files';
import {isPresent} from '../utils/lang';
import {ProfileMode} from '../../app.constants';

@Component({
  selector: 'gp-pay-slip',
  templateUrl: './employee-payslips.component.html',
  styleUrls: ['./employee-payslips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class EmployeePayslipsComponent extends Destroyable implements OnInit, OnChanges {

  @Input()
  public contractId: number;
  @Input()
  public profileMode: ProfileMode;
  @Input()
  public hideTitle = false;

  @ViewChild('externalPayslipViewer', {static: true})
  public externalPayslipViewer: PdfJsViewerComponent;

  public loading: boolean = true;
  public paySlips: FileDescription[] = [];
  public retroPayslipsByPayExecId: Map<number, Array<FileDescription>> = new Map<number, Array<FileDescription>>();
  public pagination: Pagination = new DefaultPagination();
  private sortKey: SortKey = [{
    direction: 0,
    key: 'contractPeriod'
  }];
  private downloadPayslipId$: Subject<number> = new Subject<number>();

  constructor(private cd: ChangeDetectorRef,
              private fileService: FileService) {
    super();
  }

  public ngOnInit() {
    this.pagination.update(0);

    this.pagination.pageChanged
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.fetchPaySlips());

    this.downloadPayslipId$.asObservable().pipe(
      filter(Boolean),
      switchMap((id: number) => this.fileService.getFile(id, false, true)),
      filter(Boolean),
      tap((result: { blob, fileName }) => this.downloadBlob(result)),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public downloadPayslip(id: number): void {
    this.downloadPayslipId$.next(id);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.contractId && this.contractId) {
      const contractId = this.contractId;
      this.contractId = null;
      this.load(contractId);
    }
  }

  public load(contractId: number) {
    if (!this.contractId || this.contractId !== contractId) {
      this.contractId = contractId;
      this.fetchPaySlips();
    }
  }

  public onSortKeyChange(key: SortKey): void {
    this.sortKey = key;
    this.fetchPaySlips();
  }

  public displayRetroPayslips(payslip: FileDescription) {
    if (this.retroPayslipsByPayExecId.get(payslip.payExecId).length === 0) {
      this.fetchRetroPayslips(payslip);
    } else {

      this.addRetroPayslips(payslip);
    }
  }

  public hasRetroPayslips(payslip: FileDescription): boolean {
    return payslip.tags && payslip.tags.retroPayslipCount && payslip.tags.retroPayslipCount > 0;
  }

  public isRetroPayslip(payslip: FileDescription): boolean {
    return payslip.tags.type === 'RETROACTIVITY';
  }

  public fetchRetroPayslips(payslip: FileDescription) {
    const search = `search=contractId==${this.contractId};type==PAYSLIP_PDF&tags=type==RETROACTIVITY;originPayExecId==${payslip.payExecId}`;
    this.fileService.fetchFiles(search, 10, 0, false, this.profileMode)
      .subscribe((result: FileDescriptionsWithTotal) => {
        this.retroPayslipsByPayExecId.set(payslip.payExecId, result.data);
        this.addRetroPayslips(payslip);
      });
  }

  public addRetroPayslips(payslip: FileDescription) {
    const index = this.paySlips.indexOf(payslip);
    this.paySlips[index].tags.isRetroDisplay = true;
    this.paySlips.splice(index + 1, 0, ...this.retroPayslipsByPayExecId.get(payslip.payExecId));
    this.cd.detectChanges();
  }

  public removeRetroPayslips(payslip: FileDescription) {
    const index = this.paySlips.indexOf(payslip);
    this.paySlips[index].tags.isRetroDisplay = false;
    this.paySlips = this.paySlips
      .filter(paySlip =>
        (!paySlip.tags.originPayExecId || (paySlip.tags.originPayExecId && paySlip.tags.originPayExecId !== payslip.payExecId)));
    this.cd.detectChanges();
  }

  public fetchPaySlips(): void {
    this.loading = true;
    this.paySlips = [];

    const sort: string = this.getSort();

    let orderBy: string = '';

    if (!isPresent(sort) && sort !== '') {
      orderBy = `&order-by=${sort}`;
    }

    this.pagination.resetPageIfNeeded({search: '', sort});

    const search = `search=contractId==${this.contractId};status==SENT;type==PAYSLIP_PDF&tags=type==NORMAL${orderBy}`;

    this.fileService.fetchFiles(
      search,
      this.pagination.perPage,
      this.pagination.currentPage.number,
      false,
      this.profileMode
    ).pipe(
      finalize(() => {
        this.loading = false;
        this.cd.detectChanges();
      })
    )
      .subscribe((result: FileDescriptionsWithTotal) => {
        this.paySlips = result.data;
        this.paySlips.forEach(payslip => {
          this.retroPayslipsByPayExecId.set(payslip.payExecId, []);
        });
        this.pagination.update(result.total);
      });
  }

  private downloadBlob({blob, fileName}): void {
    if (blob.type !== 'application/pdf') {
      Files.downloadBlob(blob, fileName, true);
    } else if ((navigator as any).msSaveOrOpenBlob) {
      Files.msDownloadBlob(blob, fileName);
    } else {
      this.externalPayslipViewer.pdfSrc = blob;
      this.externalPayslipViewer.downloadFileName = fileName;
      this.externalPayslipViewer.viewerTab = undefined;
      this.externalPayslipViewer.refresh();
    }
  }

  private getSort(): string {
    return this.sortKey.map(element => {
      const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
      return `${prefix}${element.key}`;
    }).join(',');
  }

  public isEssMode() {
    return this.profileMode === ProfileMode.ESS;
  }

}
